import React from "react";
import { AppButton, FigmaTypography } from "@libs/zapi-ui/custom";
import { ChevronLeftIcon, ChevronRightIcon } from "@radix-ui/react-icons";

type TPaginationControlsProps = {
  page: number;
  total: number;
  pageSize: number;
  setPage?: (page: number) => void;
};

export function PaginationControls({ page, total, pageSize, setPage }: TPaginationControlsProps) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  let startPage = Math.max(1, page - 2);
  let endPage = Math.min(totalPages, page + 2);
  if (endPage - startPage < 4) {
    if (startPage === 1) {
      endPage = Math.min(totalPages, startPage + 4);
    } else if (endPage === totalPages) {
      startPage = Math.max(1, endPage - 4);
    }
  }
  const pages: number[] = [];
  for (let i = startPage; i <= endPage; i++) {
    pages.push(i);
  }

  return (
    <div className="flex items-center justify-between w-full p-[12px] border-t-[1px] min-h-[56px]">
      <div className="flex items-center gap-2">
        <AppButton
          appearance="text"
          size="sm"
          disabled={page === 1}
          onClick={() => setPage?.(page - 1)}
          className="px-2"
        >
          <ChevronLeftIcon className="w-4 h-4 mr-1" />
          Previous
        </AppButton>
        {/* Page numbers logic */}
        {startPage > 1 && (
          <>
            <AppButton
              appearance="text"
              size="sm"
              onClick={() => setPage?.(1)}
              className={`min-w-[24px] w-[24px] h-[24px] flex items-center justify-center rounded-full px-1 text-[14px] ${
                page === 1 ? "bg-black text-white" : ""
              }`}
              style={{ width: "auto", minWidth: 24, height: 24 }}
            >
              1
            </AppButton>
            {startPage > 2 && <span className="px-1">...</span>}
          </>
        )}
        {pages.map((p) => (
          <AppButton
            key={p}
            appearance="text"
            size="sm"
            onClick={() => setPage?.(p)}
            className={`min-w-[24px] w-[24px] h-[24px] flex items-center justify-center rounded-full px-1 text-[14px] ${
              page === p ? "bg-black text-white" : ""
            }`}
            style={{ width: "auto", minWidth: 24, height: 24 }}
          >
            {p}
          </AppButton>
        ))}
        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && <span className="px-1">...</span>}
            <AppButton
              appearance="text"
              size="sm"
              onClick={() => setPage?.(totalPages)}
              className={`min-w-[24px] w-[24px] h-[24px] flex items-center justify-center rounded-full px-1 text-[14px] ${
                page === totalPages ? "bg-black text-white" : ""
              }`}
              style={{ width: "auto", minWidth: 24, height: 24 }}
            >
              {totalPages}
            </AppButton>
          </>
        )}
        <AppButton
          appearance="text"
          size="sm"
          disabled={page >= totalPages}
          onClick={() => setPage?.(page + 1)}
          className="px-2"
        >
          Next
          <ChevronRightIcon className="w-4 h-4 ml-1" />
        </AppButton>
      </div>
      <FigmaTypography className="text-zinc-700 text-[14px] whitespace-nowrap">
        Showing {Math.min(pageSize, total - (page - 1) * pageSize)} of {total} results
      </FigmaTypography>
    </div>
  );
}
